import type { Monaco } from '@monaco-editor/react'

export const QPARAM_THEME = 'qparam-light'

/** One Monarch grammar for the QParam family: .prog, .qppm, .qec, .sysprog, .machine, .sysspec. */
export function registerQParam(monaco: Monaco) {
  if (monaco.languages.getLanguages().some((l: { id: string }) => l.id === 'qparam')) return
  monaco.languages.register({ id: 'udiff' })
  monaco.languages.setMonarchTokensProvider('udiff', {
    tokenizer: {
      root: [
        [/^(\+\+\+|---).*$/, 'meta'],
        [/^@@.*$/, 'meta'],
        [/^\+.*$/, 'inserted'],
        [/^-.*$/, 'deleted'],
      ],
    },
  })
  monaco.editor.defineTheme(QPARAM_THEME, {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'inserted', foreground: '0a7f3f', fontStyle: 'bold' },
      { token: 'deleted', foreground: 'b42318', fontStyle: 'bold' },
      { token: 'meta', foreground: '64748b' },
      { token: 'annotation', foreground: '7c3aed' },
    ],
    colors: {},
  })
  monaco.languages.register({ id: 'qparam', extensions: ['.prog', '.qppm', '.qec', '.sysprog', '.machine', '.sysspec'] })
  monaco.languages.setMonarchTokensProvider('qparam', {
    defaultToken: '',
    keywords: [
      'program', 'gadget', 'spec', 'params', 'define', 'map', 'for', 'if', 'else', 'every', 'spans', 'use',
      'code', 'codes', 'stab', 'logX', 'logZ', 'machine', 'call', 'skip', 'noise', 'within', 'exit', 'resource',
    ],
    clauses: ['requires', 'assumes', 'ensures', 'outcomes', 'none', 'bound', 'exact', 'upper'],
    syscalls: [
      'frame', 'pauli_frame_update', 'route', 'decode', 'decode_syndrome', 'request_ancilla', 'release_ancilla',
      'request_magic', 'measure', 'parity', 'reset', 'prepare', 'merge', 'extract', 'split',
      'X', 'Y', 'Z', 'H', 'S', 'T', 'CX', 'CNOT', 'CZ', 'CY', 'CCX', 'Sdg',
    ],
    units: ['ns', 'total', 'us'],
    tokenizer: {
      root: [
        [/#.*$/, 'comment'],
        [/@\s*\d+\s*ns\b/, 'annotation'],
        [/\{\{/, 'string', '@ket'],
        [/\|[^|⟩\n]*⟩/, 'string'],
        [/[∀Σ]/, 'keyword'],
        [/[∧∨⊕≤≥≠¬→]/, 'operator'],
        [/\[\[\d+,\s*\d+,\s*\d+\]\]/, 'number'],
        [/\d+(\.\d+)?/, 'number'],
        [/(bits|qubits)\/us\b/, 'type'],
        [/[A-Za-z_][\w]*/, {
          cases: {
            '@keywords': 'keyword',
            '@clauses': 'keyword.flow',
            '@syscalls': 'type.identifier',
            '@units': 'type',
            '@default': 'identifier',
          },
        }],
        [/[=<>!+\-*\/%^:@]+/, 'operator'],
        [/[(){}[\]]/, '@brackets'],
      ],
      ket: [
        [/\}\}/, 'string', '@pop'],
        [/[^}]+/, 'string'],
        [/\}/, 'string'],
      ],
    },
  })
}

/** Lean 4, for the proofs review exercises show (the same id the Lean deep-dive page registers). */
export function registerLean4(monaco: Monaco) {
  registerQParam(monaco)
  if (monaco.languages.getLanguages().some((l: { id: string }) => l.id === 'lean4')) return
  monaco.languages.register({ id: 'lean4', extensions: ['.lean'] })
  monaco.languages.setMonarchTokensProvider('lean4', {
    defaultToken: '',
    tokenizer: {
      root: [
        [/\/-/, 'comment', '@block'],
        [/--.*$/, 'comment'],
        [/\b(theorem|lemma|def|abbrev|structure|inductive|instance|example|axiom)\b/, 'keyword'],
        [/\b(by|fun|have|show|exact|apply|intro|intros|refine|rfl|simp|rw|decide|native_decide|omega|cases|induction|constructor|calc|at|with|match|if|then|else|let|sorry)\b/, 'keyword.flow'],
        [/\b(import|namespace|end|open|section|variable|set_option)\b/, 'keyword'],
        [/[∀∃λ→↔∧∨¬≤≥≠⊕∑]/, 'operator'],
        [/[A-Z][\w'.]*/, 'type.identifier'],
        [/[a-z_][\w'.]*/, 'identifier'],
        [/\d+/, 'number'],
        [/"([^"\\]|\\.)*"/, 'string'],
      ],
      block: [
        [/-\//, 'comment', '@pop'],
        [/[^-]+/, 'comment'],
        [/-/, 'comment'],
      ],
    },
  })
}
